import type { AdminGame, UpdateGameRequest } from '@mcgames/contracts';
import { Button, TextInput } from '@mcgames/ui';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import { useGameMutation, useMetadataStatus } from './game-hooks.ts';
import styles from './Games.module.css';
import { IgdbPicker } from './IgdbPicker.tsx';
import { TagInput } from './TagInput.tsx';

/** The form is rebuilt from the game whenever one of the fields it edits changes on the server. */
function detailsKey(game: AdminGame): string {
  return JSON.stringify([
    game.title,
    game.description,
    game.developer,
    game.publisher,
    game.releaseYear,
    game.trailerYoutubeId,
    game.genres,
  ]);
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function DetailsTab({ game }: { game: AdminGame }) {
  const { t } = useTranslation('admin');
  const metadata = useMetadataStatus();
  const save = useGameMutation<UpdateGameRequest>(game.id, 'PATCH');
  const [igdbOpen, setIgdbOpen] = useState(false);

  return (
    <>
      {metadata.data?.igdb === true && (
        <div className={styles['row']}>
          <Button variant="secondary" onClick={() => setIgdbOpen(true)}>
            {t('games.igdb.button')}
          </Button>
        </div>
      )}
      <DetailsForm
        key={detailsKey(game)}
        game={game}
        pending={save.isPending}
        saved={save.isSuccess}
        error={save.error}
        onSubmit={(request) => save.mutate(request)}
      />
      {igdbOpen && (
        <IgdbPicker gameId={game.id} defaultQuery={game.title} onClose={() => setIgdbOpen(false)} />
      )}
    </>
  );
}

type DetailsFormProps = {
  game: AdminGame;
  pending: boolean;
  saved: boolean;
  error: unknown;
  onSubmit: (request: UpdateGameRequest) => void;
};

function DetailsForm({ game, pending, saved, error, onSubmit }: DetailsFormProps) {
  const { t } = useTranslation('admin');
  const descriptionId = useId();
  const [title, setTitle] = useState(game.title);
  const [description, setDescription] = useState(game.description);
  const [developer, setDeveloper] = useState(game.developer ?? '');
  const [publisher, setPublisher] = useState(game.publisher ?? '');
  const [releaseYear, setReleaseYear] = useState(game.releaseYear?.toString() ?? '');
  const [trailer, setTrailer] = useState(game.trailerYoutubeId ?? '');
  const [genres, setGenres] = useState(game.genres);
  // Edits after a save make the "Saved" note stale.
  const [dirty, setDirty] = useState(false);

  function edit<T>(set: (value: T) => void) {
    return (value: T) => {
      set(value);
      setDirty(true);
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setDirty(false);
    onSubmit({
      title: title.trim(),
      description,
      developer: nullIfBlank(developer),
      publisher: nullIfBlank(publisher),
      releaseYear: releaseYear === '' ? null : Number(releaseYear),
      trailerYoutubeId: nullIfBlank(trailer),
      genres,
    });
  }

  return (
    <form className={styles['form']} onSubmit={submit}>
      <TextInput
        label={t('games.details.title')}
        value={title}
        required
        onChange={(event) => edit(setTitle)(event.target.value)}
      />
      <div className={styles['row']}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.details.developer')}
            value={developer}
            onChange={(event) => edit(setDeveloper)(event.target.value)}
          />
        </div>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.details.publisher')}
            value={publisher}
            onChange={(event) => edit(setPublisher)(event.target.value)}
          />
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.details.releaseYear')}
            type="number"
            min={1970}
            max={2100}
            value={releaseYear}
            onChange={(event) => edit(setReleaseYear)(event.target.value)}
          />
        </div>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.details.trailer')}
            value={trailer}
            onChange={(event) => edit(setTrailer)(event.target.value)}
          />
        </div>
      </div>
      <div className={styles['fieldset']}>
        <label className={styles['fieldLabel']} htmlFor={descriptionId}>
          {t('games.details.description')}
        </label>
        <textarea
          id={descriptionId}
          className={styles['textarea']}
          value={description}
          onChange={(event) => edit(setDescription)(event.target.value)}
        />
      </div>
      <TagInput
        legend={t('games.details.genres')}
        inputLabel={t('games.details.newGenre')}
        hint={t('games.details.genreHint')}
        removeLabel={(genre) => t('games.details.removeGenre', { genre })}
        value={genres}
        onChange={edit(setGenres)}
      />
      <ErrorText error={error} />
      <div className={styles['row']}>
        <Button type="submit" disabled={pending || title.trim() === ''}>
          {t('games.details.save')}
        </Button>
        <p role="status" className={styles['status']}>
          {saved && !dirty && !pending ? t('games.details.saved') : ''}
        </p>
      </div>
    </form>
  );
}
